import React from "react";
import {
    Box,
    Typography,
    Paper,
    Stack,
    FormControlLabel,
    Button,
} from "@mui/material";
import SquareCheckbox from "../filters/squareCheckbox";

export default function OrderSummarySection({
                                                t,
                                                formik,
                                                items = [],
                                                subtotal = 0,
                                                tax = 0,
                                                total = 0,
                                                sum = 0,
                                                shippingLabel = "",
                                                loading = false,
                                            }) {
    const orderItems = items.map((item) => ({
        id: item.id,
        imageUrl: item.image,
        quantityLabel: `${item.quantity}x`,
        sku: item.code ? `ΚΩΔΙΚΟΣ: ${item.code}` : "",
        name: item.title,
        price: `${Number(item.price || 0).toFixed(2)} €`,
    }));

    const ORDER_SUMMARY_TITLE = t("My order");

    const orderTotals = [
        {
            key: "subtotal",
            label: t("Value without tax"),
            value: `${Number(sum || 0).toFixed(2)} €`,
            isGrandTotal: false,
        },
        {
            key: "shipping",
            label: t("Shipping"),
            value: shippingLabel,
            isGrandTotal: false,
        },
        {
            key: "tax",
            label: t("Vat"),
            value: `${Number(tax || 0).toFixed(2)} €`,
            isGrandTotal: false,
        },
        {
            key: "total",
            label: t("Total"),
            value: `${Number(total || 0).toFixed(2)} €`,
            isGrandTotal: true,
        },
    ];

    const TERMS_TEXT = t(
        "I have read and accept the Terms of Service and the Privacy Policy."
    );

    const CONFIRM_BUTTON_LABEL = t("Confirm order");

    const acceptedTerms = formik.values.acceptTerms;

    return (
        <Box
            sx={{
                width: "100%",
                maxWidth: 495,
                display: "flex",
                flexDirection: "column",
            }}
        >
            <Typography
                sx={{
                    fontFamily: "'Zen Maru Gothic', sans-serif",
                    fontWeight: 900,
                    fontSize: 17,
                    lineHeight: "110%",
                    textTransform: "uppercase",
                    color: "#111111",
                    mb: 2,
                    width: "100%",
                }}
            >
                {ORDER_SUMMARY_TITLE}
            </Typography>

            <Box
                sx={{
                    width: "100%",
                    maxWidth: 495,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                }}
            >
                <Stack spacing={0.5}>
                    {orderItems.map((item) => (
                        <Paper
                            key={item.id}
                            sx={{
                                p: 1.25,
                                borderRadius: "10px",
                                backgroundColor: "#E9E9E9",
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 81.92,
                                    height: 72.54,
                                    borderRadius: "5px",
                                    backgroundImage: `url(${item.imageUrl})`,
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                    flexShrink: 0,
                                }}
                            />

                            <Box
                                sx={{
                                    flex: 1,
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: "'Zen Maru Gothic', sans-serif",
                                        fontSize: 16,
                                        fontWeight: 700,
                                        lineHeight: "120%",
                                        color: "#E5007E",
                                    }}
                                >
                                    {item.quantityLabel}
                                </Typography>

                                <Typography
                                    sx={{
                                        fontFamily: "'Zen Maru Gothic', sans-serif",
                                        fontSize: 14,
                                        fontWeight: 700,
                                        lineHeight: "90%",
                                        textTransform: "uppercase",
                                        color: "#111111",
                                    }}
                                >
                                    {item.sku}
                                </Typography>

                                <Typography
                                    sx={{
                                        mt: 0.5,
                                        fontFamily: "'Zen Maru Gothic', sans-serif",
                                        fontSize: 14,
                                        lineHeight: "120%",
                                        color: "#111111",
                                    }}
                                >
                                    {item.name}
                                </Typography>
                            </Box>

                            <Typography
                                sx={{
                                    fontFamily: "'Zen Maru Gothic', sans-serif",
                                    fontSize: 16,
                                    fontWeight: 700,
                                    lineHeight: "120%",
                                    minWidth: 75,
                                    textAlign: "right",
                                    color: "#111111",
                                }}
                            >
                                {item.price}
                            </Typography>
                        </Paper>
                    ))}
                </Stack>

                <Paper
                    sx={{
                        p: 1.25,
                        borderRadius: "10px",
                        backgroundColor: "#E9E9E9",
                    }}
                >
                    <Stack spacing={1.5}>
                        {orderTotals.map((row) => (
                            <Box key={row.key}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 2,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontFamily: "'Zen Maru Gothic', sans-serif",
                                            fontSize: row.isGrandTotal ? 20 : 16,
                                            fontWeight: row.isGrandTotal ? 700 : 400,
                                            lineHeight: "120%",
                                            color: "#111111",
                                        }}
                                    >
                                        {row.label}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontFamily: "'Zen Maru Gothic', sans-serif",
                                            fontSize: row.isGrandTotal ? 20 : 16,
                                            fontWeight: row.isGrandTotal ? 700 : 400,
                                            lineHeight: "120%",
                                            textAlign: "right",
                                            color: "#111111",
                                        }}
                                    >
                                        {row.value}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                </Paper>

                <Box>
                    <FormControlLabel
                        control={
                            <SquareCheckbox
                                checked={acceptedTerms}
                                onChange={(e) =>
                                    formik.setFieldValue("acceptTerms", e.target.checked)
                                }
                                sx={{
                                    p: 0,
                                    mr: 1.25,
                                    "& .MuiSvgIcon-root": {fontSize: 18},
                                }}
                            />
                        }
                        label={
                            <Typography
                                sx={{
                                    fontFamily: "'Zen Maru Gothic', sans-serif",
                                    fontSize: 14,
                                    lineHeight: "120%",
                                    color: "#111111",
                                }}
                            >
                                {TERMS_TEXT}
                            </Typography>
                        }
                        sx={{m: 0}}
                    />

                    {formik.touched.acceptTerms && formik.errors.acceptTerms && (
                        <Typography
                            sx={{
                                mt: 0.5,
                                fontSize: 12,
                                color: "error.main",
                            }}
                        >
                            {formik.errors.acceptTerms}
                        </Typography>
                    )}
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: {xs: "stretch", md: "center"},
                    }}
                >
                    <Button
                        variant="contained"
                        disabled={!acceptedTerms || loading}
                        onClick={formik.handleSubmit}
                        sx={{
                            mt: 1,
                            px: 4,
                            py: 1.5,
                            borderRadius: "80px",
                            textTransform: "uppercase",
                            fontFamily: "'Zen Maru Gothic', sans-serif",
                            fontWeight: 900,
                            fontSize: 16,
                            lineHeight: "120%",
                            backgroundColor: "#E5007E",
                            color: "#FCFCFC",
                            "&:hover": {
                                backgroundColor: "#c40068",
                            },
                        }}
                    >
                        {CONFIRM_BUTTON_LABEL}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}